import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, IconButton, Paper, Typography} from '@mui/material';
import {CallEnd, Close, Mic, MicOff} from '@mui/icons-material';
import {FELICITY_VOICE_URL} from '../../constants/felicity';
import useFelicityPass from './useFelicityPass';
import {readShow, readWalk, stripSentinel} from './replyBlocks';
import {describeScreen, startGuide, stopGuide, withNamedGroup} from './screenGuide';

// AD-2653: the topic the browser and the avatar share. The browser sends the names on the screen and
// "done" when a walkthrough step is finished; the avatar sends each answer so its steps are outlined.
const GUIDE_TOPIC = 'felicity-guide';
const SCREEN_CHECK_MS = 1000;

/**
 * AD-2653: one avatar session for the whole visit. The panel moves into a window when one opens, such as
 * Create new group, which draws it afresh. The session carries on through that, and ends only when the
 * person presses End, closes Felicity or goes back to typing. Without this, every window began a new
 * session and Felicity greeted the person again.
 */
const live = {room: null, starting: false, videoTrack: null, videoEl: null, screenTimer: null, micOn: false, setStatus: null};

// AD-2653: the room is kept for the visit, so a page that loads afresh rejoins it and the conversation carries on.
export const AVATAR_ROOM_KEY = 'felicity-avatar-room';
const ROOM_KEPT_MS = 25 * 60 * 1000;

const keptRoom = () => {
    try {
        const kept = JSON.parse(sessionStorage.getItem(AVATAR_ROOM_KEY) || 'null');
        return kept?.url && kept?.token && Date.now() - kept.at < ROOM_KEPT_MS ? kept : null;
    } catch {
        return null;
    }
};

/** A page loaded afresh while the avatar was open shows her again. */
export const avatarWasOpen = () => Boolean(keptRoom());

const keepRoom = (url, token) => {
    try {
        sessionStorage.setItem(AVATAR_ROOM_KEY, JSON.stringify({url, token, at: Date.now()}));
    } catch {
        // Without it, a page that loads afresh starts a new conversation.
    }
};

const forgetRoom = () => {
    try {
        sessionStorage.removeItem(AVATAR_ROOM_KEY);
    } catch {
        // Nothing was kept.
    }
};

const endLive = async () => {
    forgetRoom();
    clearInterval(live.screenTimer);
    stopGuide();
    const room = live.room;
    Object.assign(live, {room: null, videoTrack: null, screenTimer: null, micOn: false});
    try {
        await room?.disconnect();
    } catch {
        // The room had already gone.
    }
};

const PANEL_STYLE = {
    position: 'fixed',
    bottom: 96,
    right: 24,
    zIndex: 1300,
    // The video keeps its 16:9 shape, so on a short screen the panel is made narrower until the video,
    // the heading and the buttons fit under the top bar (Maria, 8 October 2026: cut off on a laptop).
    width: 'min(680px, calc((100vh - 230px) * 16 / 9))',
    minWidth: 320,
    maxWidth: 'calc(100vw - 48px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 3
};

const HEADER_STYLE = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1.5,
    px: 2.5,
    py: 2,
    borderBottom: '1px solid',
    borderColor: 'divider'
};

const STAGE_STYLE = {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    backgroundColor: 'action.hover'
};

const OVERLAY_STYLE = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    px: 3,
    textAlign: 'center'
};

const FOOTER_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    px: 2,
    py: 1.5,
    borderTop: '1px solid',
    borderColor: 'divider'
};

/**
 * Felicity as an avatar. A LiveKit room is asked for from Felicity's own service, the school
 * avatar agent joins it, and every answer spoken comes from the knowledge base for the roles on
 * the signed-in account.
 *
 * She starts when the person presses Talk to Felicity, so no microphone is touched on page load.
 */
const FelicityVoicePanel = ({onType, onClose, accountType, setup, panelRef, offset}) => {
    const passFor = useFelicityPass();
    const [status, setStatus] = useState('idle');
    const [note, setNote] = useState('');
    const [micOn, setMicOn] = useState(false);
    const videoRef = useRef(null);

    const send = (room, message) => room.localParticipant
        .publishData(new TextEncoder().encode(JSON.stringify(message)), {reliable: true, topic: GUIDE_TOPIC})
        .catch(() => {
            // The room has gone; the next answer starts again.
        });

    // The names on the screen go to the avatar whenever they change, so she can walk the person through a task.
    const followScreen = (room) => {
        let sent = '';
        clearInterval(live.screenTimer);
        live.screenTimer = setInterval(() => {
            const screen = describeScreen();
            if (screen !== sent) {
                sent = screen;
                send(room, {type: 'screen', screen, setup});
            }
        }, SCREEN_CHECK_MS);
    };

    const stop = async () => {
        await endLive();
        setStatus('idle');
        setMicOn(false);
    };

    // The panel drawn afresh takes over the session that is already running.
    useEffect(() => {
        live.setStatus = setStatus;
        live.videoEl = videoRef.current;
        return () => {
            live.setStatus = null;
            live.videoEl = null;
            // A panel that is closed, rather than moved into a window, is not drawn again, so its session ends.
            setTimeout(() => {
                if (!live.setStatus) {
                    endLive();
                }
            }, 1500);
        };
    }, []);

    const attach = (livekit, room) => {
        room.on(livekit.RoomEvent.TrackSubscribed, track => {
            if (track.kind === 'video') {
                live.videoTrack = track;
                if (live.videoEl) {
                    track.attach(live.videoEl);
                }
                live.setStatus?.('live');
                return;
            }
            if (track.kind === 'audio') {
                const element = track.attach();
                element.autoplay = true;
                document.body.appendChild(element);
            }
        });
        room.on(livekit.RoomEvent.DataReceived, (payload, participant, kind, topic) => {
            if (topic !== GUIDE_TOPIC) {
                return;
            }
            try {
                const {type, text} = JSON.parse(new TextDecoder().decode(payload));
                if (type === 'answer') {
                    startGuide(withNamedGroup(readShow(text), stripSentinel(text)), {walk: readWalk(text), onDone: () => send(room, {type: 'done'})});
                }
            } catch {
                // A message this page does not read.
            }
        });
        room.on(livekit.RoomEvent.Disconnected, () => {
            clearInterval(live.screenTimer);
            stopGuide();
            Object.assign(live, {room: null, videoTrack: null, screenTimer: null, micOn: false});
            live.setStatus?.('idle');
        });
    };

    const start = async () => {
        // A panel drawn afresh while the first is still joining waits for that session, never starts a second.
        if (live.starting) {
            setStatus('connecting');
            return;
        }

        if (live.room) {
            if (live.videoTrack && videoRef.current) {
                live.videoTrack.attach(videoRef.current);
                setStatus('live');
            }
            setMicOn(live.micOn);
            return;
        }

        if (!FELICITY_VOICE_URL) {
            setNote('Felicity is not connected yet.');
            setStatus('error');
            return;
        }

        setStatus('connecting');
        setNote('');
        live.starting = true;

        const askForRoom = async () => {
            const pass = await passFor();
            const response = await fetch(FELICITY_VOICE_URL, {
                method: 'POST',
                credentials: 'include',
                headers: pass
                    ? {'Content-Type': 'application/json', 'x-admicity-felicity-pass': pass}
                    : {'Content-Type': 'application/json'},
                body: JSON.stringify({accountType})
            });
            const body = await response.json().catch(() => ({}));

            if (!response.ok || !body?.data?.url || !body?.data?.token) {
                throw Object.assign(new Error('refused'), {note: body?.error});
            }

            return body.data;
        };

        const join = async ({url, token}) => {
            const livekit = await import('livekit-client');
            const room = new livekit.Room({adaptiveStream: false, dynacast: false});

            live.room = room;
            attach(livekit, room);
            await room.connect(url, token);
            keepRoom(url, token);
        };

        try {
            // A page loaded afresh rejoins the room it had, so Felicity carries on the same conversation.
            const kept = keptRoom();
            try {
                await join(kept ?? await askForRoom());
            } catch (error) {
                if (!kept || error?.note) {
                    throw error;
                }
                forgetRoom();
                live.room = null;
                await join(await askForRoom());
            }
            live.starting = false;
            followScreen(live.room);

            try {
                await live.room.localParticipant.setMicrophoneEnabled(true);
                live.micOn = true;
                setMicOn(true);
            } catch {
                setNote('Your browser is blocking the microphone. Allow it to talk back to Felicity.');
            }
        } catch (error) {
            live.starting = false;
            setNote(error?.note || 'Felicity could not start just now.');
            setStatus('error');
            await stop();
        }
    };

    /**
     * Pressing Talk to Felicity is the person asking for her, so she starts at once. The timer is
     * cleared if the panel closes first, so a panel opened and shut again joins no room.
     */
    useEffect(() => {
        const timer = setTimeout(start, 0);
        return () => clearTimeout(timer);
    }, []);

    const toggleMic = async () => {
        const room = live.room;

        if (!room?.localParticipant) {
            return;
        }

        try {
            await room.localParticipant.setMicrophoneEnabled(!micOn);
            live.micOn = !micOn;
            setMicOn(!micOn);
            setNote('');
        } catch {
            setNote('Your browser is blocking the microphone.');
        }
    };

    return (
        <Paper elevation={8} ref={panelRef} data-felicity="voice" sx={{...PANEL_STYLE, ...offset}}>
            <Box sx={HEADER_STYLE}>
                <Box>
                    <Typography variant="subtitle2" fontWeight={600}>Felicity</Typography>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            endLive();
                            onType();
                        }}
                        sx={{flex: 'none', borderRadius: 8, textTransform: 'none'}}
                    >
                        Type to Felicity
                    </Button>
                    <IconButton
                        size="small"
                        onClick={() => {
                            endLive();
                            onClose();
                        }}
                        aria-label="Close Felicity"
                    >
                        <Close fontSize="small"/>
                    </IconButton>
                </Box>
            </Box>

            <Box sx={STAGE_STYLE}>
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: status === 'live' ? 'block' : 'none'
                    }}
                />
                {status !== 'live' && (
                    <Box sx={OVERLAY_STYLE}>
                        {status === 'connecting'
                            ? <Typography variant="body2" color="text.secondary">Felicity is joining…</Typography>
                            : (
                                <>
                                    <Button variant="contained" onClick={start} sx={{borderRadius: 8}}>
                                        Start talking to Felicity
                                    </Button>
                                </>
                            )}
                        {note && <Typography variant="caption" color="error">{note}</Typography>}
                    </Box>
                )}
            </Box>

            <Box sx={FOOTER_STYLE}>
                <Box/>
                {status === 'live' && (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <IconButton
                            size="small"
                            onClick={toggleMic}
                            aria-label={micOn ? 'Turn the microphone off' : 'Turn the microphone on'}
                        >
                            {micOn ? <Mic fontSize="small"/> : <MicOff fontSize="small"/>}
                        </IconButton>
                        <Button
                            size="small"
                            color="error"
                            variant="contained"
                            startIcon={<CallEnd/>}
                            onClick={stop}
                            sx={{borderRadius: 8, textTransform: 'none'}}
                        >
                            End
                        </Button>
                    </Box>
                )}
            </Box>

            {status === 'live' && note && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider'}}
                >
                    {note}
                </Typography>
            )}
        </Paper>
    );
};

FelicityVoicePanel.propTypes = {
    onType: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    accountType: PropTypes.string,
    setup: PropTypes.string,
    panelRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),
    offset: PropTypes.object
};

export default FelicityVoicePanel;
